/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, BoxProps, Fade, makeStyles } from '@material-ui/core';
import transitions from '@material-ui/core/styles/transitions';
import React, {
  createContext,
  DetailedHTMLProps,
  FormHTMLAttributes,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import DotsProgress from '../DotsProgress';

export type FormProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any
> = {
  form?: UseFormReturn<TFieldValues, TContext>;
  onFormSubmit?: FormSubmitEventHandler<TFieldValues, TContext>;
  loading?: boolean;
  disabled?: boolean;
  marginDense?: boolean;
} & React.FormHTMLAttributes<HTMLFormElement> &
  BoxProps;

export type FormSubmitEventHandler<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any
> = (
  values: TFieldValues,
  isValid: boolean,
  form: UseFormReturn<TFieldValues, TContext>
) => void;

const useStyles = makeStyles({
  root: {
    position: 'relative',
    '&.disabled': {
      pointerEvents: 'none',
    },
    '&.loading': {
      pointerEvents: 'none',
      '& .MuiFormControl-root,& .MuiButton-root,& .file-droparea': {
        filter: 'blur(1px) opacity(0.2)',
        transition: transitions.create('filter', { duration: 300 }),
      },
    },
    '&:not(.dense) .MuiFormControl-root': {
      marginBottom: '1rem',
      '& .MuiFormLabel-root': {
        marginBottom: '0.3rem',
      },
    },
    '& .backdrop': {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 99,
      backdropFilter: 'blur(4px)',
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const Form = <TFieldValues extends FieldValues = FieldValues, TContext = any>({
  children,
  onFormSubmit,
  onSubmit,
  style,
  form,
  loading,
  disabled,
  className,
  marginDense,
  ...props
}: FormProps<TFieldValues, TContext>) => {
  const classes = useStyles();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = ev => {
    ev.preventDefault();
    if (form) {
      form.clearErrors();
      form
        .handleSubmit(
          values => onFormSubmit?.(values, true, form),
          () => onFormSubmit?.(form.getValues(), false, form)
        )(ev)
        .catch(noop);
    } else {
      onSubmit?.(ev);
    }
  };

  return (
    <Box
      className={`${classes.root} ${className} \
      ${loading && 'loading'} ${disabled && 'disabled'} ${
        marginDense && 'dense'
      }`}
      component={FormComponent}
      onSubmit={handleSubmit}
      display="flex"
      flexDirection="column"
      style={{ ...style }}
      {...props}
    >
      {form ? (
        <FormProvider {...form}>{children}</FormProvider>
      ) : (
        <>{children}</>
      )}
      <Fade in={!!loading} mountOnEnter unmountOnExit>
        <div className="backdrop">
          <DotsProgress size="sm" />
        </div>
      </Fade>
    </Box>
  );
};

export default Form;

type SubmitFn = () => void;
/**
 * a convenient hook to access the form submit function
 */
export const useFormSubmit = (): SubmitFn => useContext(FormSubmitFn);

const FormSubmitFn = createContext<SubmitFn>(noop);

const FormComponent: React.FC<
  DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>
> = ({ children, ...props }) => {
  const ref = useRef<HTMLFormElement>(null);
  const submitFn = useCallback(
    () => setTimeout(() => ref.current?.requestSubmit(), 0),
    []
  );

  return (
    <form ref={ref} {...props}>
      <FormSubmitFn.Provider value={submitFn}>{children}</FormSubmitFn.Provider>
    </form>
  );
};
